/**
 * Whether a company can actually take a card right now.
 *
 * Connecting a payment account is not a switch, it is a journey with a middle:
 * nobody has started, somebody started and stopped, Stripe wants another
 * document, everything works, something got restricted last Tuesday. Treating
 * that as a boolean is how a technician ends up standing in a kitchen tapping
 * a button that was never going to work.
 *
 * So the states are named, derived in one place, and shared. The web app, both
 * phone apps and the API all ask this function rather than each inventing its
 * own idea of "connected" out of two flags and an optimistic `??`.
 *
 * Deliberately a pure function over data the server already publishes. It
 * makes no network call, so it is as true as the last webhook — see
 * `syncedAt`, which exists so that a stale answer is visible rather than
 * silently believed.
 */
import { applyBps } from './money.js';
/**
 * `everCharged` distinguishes "not finished yet" from "stopped working".
 *
 * Both look identical in Stripe's flags — `charges_enabled: false` — but they
 * are different conversations. The first is "finish signing up"; the second is
 * "something is wrong, the office needs to deal with it". Without it, a
 * restricted account reads as though the contractor simply never started,
 * which is the kind of wrong that wastes a support call.
 */
export function paymentsAccount(facts, everCharged = false) {
    if (!facts.accountId) {
        return { state: 'not_connected', canCharge: false, canPayout: false };
    }
    if (facts.chargesEnabled) {
        return { state: 'enabled', canCharge: true, canPayout: facts.payoutsEnabled };
    }
    return {
        state: everCharged ? 'restricted' : 'onboarding',
        canCharge: false,
        canPayout: false,
    };
}
/**
 * Whether the cached flags are old enough to distrust.
 *
 * The flags are a mirror of Stripe's, updated by webhook. Webhooks get missed.
 * This does not change what the app allows — refusing payments because an
 * event was late would be its own outage — but it lets a screen say "last
 * checked three days ago" instead of presenting a stale answer as fact.
 */
export function paymentsAccountIsStale(syncedAt, now = new Date(), maxAgeHours = 24) {
    if (!syncedAt)
        return true;
    const at = new Date(syncedAt);
    if (Number.isNaN(at.getTime()))
        return true;
    return now.getTime() - at.getTime() > maxAgeHours * 3_600_000;
}
/**
 * The mode a secret key operates in, or null if it is not a key.
 *
 * Read from the prefix rather than by asking Stripe, because this has to work
 * on a server that cannot reach Stripe, and because the prefix is the one part
 * of a key that is documented to mean something.
 */
export function stripeModeFromKey(key) {
    if (!key)
        return null;
    if (key.startsWith('sk_live_') || key.startsWith('rk_live_'))
        return 'live';
    if (key.startsWith('sk_test_') || key.startsWith('rk_test_'))
        return 'test';
    return null;
}
/**
 * Whether a stored connected account is one this server can still act on.
 *
 * Returns true when there is nothing to doubt — no account, or an account
 * whose mode matches the key in hand. The interesting answer is false, which
 * means: an account exists on paper and is unreachable, so every screen should
 * behave as though it is not there and the contractor should be allowed to
 * start again.
 *
 * An unknown current mode — no key configured — answers true. A server with no
 * Stripe key cannot take payments at all, and inventing a "start over" prompt
 * out of a missing environment variable would be a worse failure than the one
 * being prevented.
 */
export function connectAccountIsReachable(stored, current) {
    if (!stored.accountId)
        return true;
    if (!current)
        return true;
    return stored.mode === current;
}
/** What to say on a screen. Written for a contractor, not an engineer. */
export const PAYMENTS_ACCOUNT_LABELS = {
    not_connected: 'Not set up',
    onboarding: 'Finish setting up',
    restricted: 'Needs attention',
    enabled: 'Ready to take payments',
};
/**
 * The platform's cut of a charge, in cents.
 *
 * Integer arithmetic over basis points, not a float times a percentage: this
 * is a number somebody is owed, and 0.1 + 0.2 is not 0.3.
 *
 * Clamped to the charge itself. A fee larger than the payment is not a
 * pricing decision, it is a mistake — a misplaced decimal in a config — and
 * the result would be a connected account that goes negative on a successful
 * sale. Stripe would reject it anyway; refusing here means the error is about
 * the configuration rather than the customer's card.
 */
export function platformFeeCents(amountCents, feeBps) {
    if (amountCents <= 0 || feeBps <= 0)
        return 0;
    const fee = applyBps(amountCents, feeBps);
    return fee > amountCents ? amountCents : fee;
}
//# sourceMappingURL=payments-account.js.map